import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import FilterIcon from '@mui/icons-material/FilterAlt';
import { ClickAwayListener, IconButton, Paper, Popper, Tooltip } from '@mui/material';

import { ReactComponent as BackIcon } from '@/assets/icons/ArrowLeftCustom.svg';
import { useGoBack } from '@/shared/lib/hooks/useGoBack';

import {
  CardContent,
  CountText,
  FilterChipStyled,
  FilterChipText,
  FilterChipValue,
  FiltersCard,
  FiltersContainer,
  HeaderRoot,
  IconWrapper,
  Label,
  OverflowFiltersContainer,
  RemoveButton,
  TitleCard,
  ViewMoreButton,
  overflowPopperPaperSx,
  overflowPopperStyle,
} from './GeneratikTableHeader.styles';
import { FilterChip, GeneratikTableHeaderProps } from './GeneratikTableHeader.types';

const FilterChipContent = ({
  filter,
  onRemove,
}: {
  filter: FilterChip;
  onRemove?: (id: string) => void;
}) => {
  const valueRef = useRef<HTMLSpanElement>(null);
  const [tooltipEnabled, setTooltipEnabled] = useState(false);

  const handleMouseEnter = () => {
    const el = valueRef.current;
    if (el) {
      setTooltipEnabled(el.scrollWidth > el.clientWidth);
    }
  };

  return (
    <>
      <Tooltip
        title={
          tooltipEnabled ? (filter.label ? `${filter.label}: ${filter.value}` : filter.value) : ''
        }
        placement="top"
        arrow
        disableInteractive
      >
        <FilterChipText onMouseEnter={handleMouseEnter}>
          {filter.label ? <>{filter.label}: </> : null}
          <FilterChipValue ref={valueRef}>{filter.value}</FilterChipValue>
        </FilterChipText>
      </Tooltip>
      {onRemove && filter.removable !== false && (
        <RemoveButton
          size="small"
          onClick={() => onRemove(filter.id)}
          data-testid={`filter-chip-remove-${filter.id}`}
        >
          <CloseIcon />
        </RemoveButton>
      )}
    </>
  );
};

const GeneratikTableHeader = ({
  title,
  titleIcon,
  filteredCount,
  totalCount,
  filters = [],
  onFilterRemove,
  onFilterClick,
  hideFilters = false,
  isModal = false,
  hasBackButton = false,
}: GeneratikTableHeaderProps) => {
  const { t } = useTranslation();
  const goBack = useGoBack();

  const containerRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleCount, setVisibleCount] = useState(filters.length);
  const [popperAnchor, setPopperAnchor] = useState<HTMLElement | null>(null);

  const handleFilterRemove = useCallback(
    (filterId: string) => {
      onFilterRemove?.(filterId);
    },
    [onFilterRemove],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || filters.length === 0) {
      setVisibleCount(filters.length);
      return;
    }

    const calculateVisible = () => {
      const containerWidth = container.offsetWidth;
      const gap = 10;
      const moreButtonWidth = 80;
      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < chipsRef.current.length; i++) {
        const chip = chipsRef.current[i];
        if (!chip) continue;
        const chipWidth = chip.offsetWidth;
        const nextWidth = usedWidth + chipWidth + (i > 0 ? gap : 0);

        if (nextWidth > containerWidth) break;

        const isLast = i === filters.length - 1;
        if (!isLast && nextWidth + gap + moreButtonWidth > containerWidth) break;

        usedWidth = nextWidth;
        count++;
      }

      setVisibleCount(count || 1);
    };

    requestAnimationFrame(calculateVisible);

    const observer = new ResizeObserver(calculateVisible);
    observer.observe(container);
    return () => observer.disconnect();
  }, [filters]);

  const hasFilters = filters.length > 0;
  const showFiltersSection = !hideFilters && (hasFilters || filteredCount !== totalCount);
  const overflowCount = filters.length - visibleCount;
  const hasOverflow = overflowCount > 0;

  const handleViewMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setPopperAnchor(popperAnchor ? null : event.currentTarget);
  };

  const handlePopperClose = () => {
    setPopperAnchor(null);
  };

  return (
    <HeaderRoot>
      {!isModal && !!title && (
        <TitleCard hasBackButton={hasBackButton}>
          <CardContent>
            {hasBackButton && (
              <IconButton onClick={goBack} data-testid="page-back-button">
                <BackIcon width={17} height={19} />
              </IconButton>
            )}

            {titleIcon && <IconWrapper>{titleIcon}</IconWrapper>}
            <Label data-testid="page-title">
              {title}
              {filteredCount !== undefined && totalCount !== undefined ? ':' : ''}
            </Label>
            {filteredCount !== undefined && totalCount !== undefined && (
              <CountText>
                {filteredCount} / {totalCount}
              </CountText>
            )}
          </CardContent>
        </TitleCard>
      )}

      <FiltersCard isModal={isModal}>
        <CardContent>
          <IconWrapper>
            <FilterIcon />
          </IconWrapper>

          <Label>{t('Filters')}:</Label>
          {showFiltersSection ? (
            <FiltersContainer ref={containerRef} data-testid="active-filters">
              {filters.map((filter, index) => (
                <FilterChipStyled
                  key={filter.id}
                  ref={(el: HTMLDivElement | null) => {
                    chipsRef.current[index] = el;
                  }}
                  sx={
                    index >= visibleCount
                      ? { position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }
                      : undefined
                  }
                  clickable={!!onFilterClick}
                  onClick={onFilterClick ? () => onFilterClick(filter.id) : undefined}
                >
                  <FilterChipContent
                    filter={filter}
                    onRemove={onFilterRemove ? handleFilterRemove : undefined}
                  />
                </FilterChipStyled>
              ))}
              {hasOverflow && (
                <ViewMoreButton onClick={handleViewMoreClick}>
                  +{overflowCount} {t('more')}
                </ViewMoreButton>
              )}
            </FiltersContainer>
          ) : (
            <Label>{t('none')}</Label>
          )}
        </CardContent>
      </FiltersCard>

      {hasOverflow && (
        <Popper
          open={Boolean(popperAnchor)}
          anchorEl={popperAnchor}
          placement="bottom-end"
          style={overflowPopperStyle}
        >
          <ClickAwayListener onClickAway={handlePopperClose}>
            <Paper elevation={3} sx={overflowPopperPaperSx}>
              <OverflowFiltersContainer>
                {filters.slice(visibleCount).map((filter) => (
                  <FilterChipStyled
                    key={filter.id}
                    clickable={!!onFilterClick}
                    onClick={onFilterClick ? () => onFilterClick(filter.id) : undefined}
                  >
                    <FilterChipContent
                      filter={filter}
                      onRemove={onFilterRemove ? handleFilterRemove : undefined}
                    />
                  </FilterChipStyled>
                ))}
              </OverflowFiltersContainer>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </HeaderRoot>
  );
};

export default GeneratikTableHeader;
